import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history, getIntl } from '@umijs/max';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  message?: string;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
const isDev = process.env.NODE_ENV === 'development';

export const errorConfig: RequestConfig = {
  baseURL: isDev ? '' : API_BASE_URL,
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType, message: msg } =
        res as unknown as ResponseStructure;
      if (!success) {
        const displayMessage = errorMessage || msg || 'Request failed';
        const error: any = new Error(displayMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: displayMessage, showType, data: data ?? res };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
        throw error;
      }
      if (error.config?.url === '/authenticate') {
        history.push('/404');
      }
      if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // 对于登录接口的 401 和 403 错误，不显示状态码提示（由登录页面自己处理）
        if (error.config?.url?.includes('/api/auth/login') && 
            (error.response.status === 401 || error.response.status === 403)) {
          // 不显示错误提示，由登录页面处理
          return;
        }
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const url = config?.url?.concat('');

      // 为API请求添加Authorization header
      if (url?.startsWith('/api/') || url?.startsWith('/webhook/')) {
        // 优先使用环境变量的API_TOKEN，如果没有则使用localStorage中的token
        const envApiToken = API_TOKEN;  // API_TOKEN通过webpack define传递
        const localToken = localStorage.getItem('auth_token');
        const token = envApiToken || localToken;

        if (token) {
          // 确保headers对象存在
          if (!config.headers) {
            config.headers = {};
          }
          // 添加Authorization header
          config.headers['Authorization'] = `Bearer ${token}`;
          console.log(`[Request Interceptor] 添加Authorization header for ${url}, token source: ${envApiToken ? 'env' : 'localStorage'}, token: ${token.substring(0, 20)}...`);
        } else {
          console.warn(`[Request Interceptor] 未找到API_TOKEN或auth_token for ${url}`);
        }
      }

      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        try {
          message.error(getIntl().formatMessage({ id: 'app.request.error' }));
        } catch {
          message.error('Request failed');
        }
      }
      return response;
    },
  ],
};
